import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';
import { ArrowRight, Lightbulb, MessageSquare, Users, Zap, Quote, Star, TrendingUp, BookOpen, ExternalLink } from 'lucide-react';
import ajayyaImage from '@/assets/ajayya-original.jpg';
import businessGitaCover from '@/assets/business-gita-cover.webp';

const sampleSessions = [
  {
    title: "My team is obsessed with tools but has no process",
    preview: "We're implementing a new system but it's chaotic. Everyone's chasing the perfect tool without defining how work should flow. Classic 'tools without process' syndrome...",
    tags: ["Process", "Leadership"],
    upvotes: 184,
  },
  {
    title: "Should I chase the trending career or follow my passion?",
    preview: "A friend was passionate about cooking and started a kitchen before cloud kitchens even existed. His passion made him a pioneer. Success follows passion, not trends...",
    tags: ["Career", "Strategy"],
    upvotes: 256,
  },
  {
    title: "I got promoted but didn't get the title I wanted",
    preview: "Despite hard work and dedication, the title wasn't what I hoped for. But leadership isn't about titles. It's about impact, influence, and inspiration...",
    tags: ["Leadership", "Growth"],
    upvotes: 128,
  },
];

const principles = [
  { name: "Clarity Over Comfort", description: "Facing hard truths leads to better decisions" },
  { name: "Impact Over Titles", description: "Leadership is measured by influence, not designations" },
  { name: "Passion Over Trends", description: "Pioneers follow passion, not the crowd" },
  { name: "Process Before Tools", description: "Build the foundation before adding technology" },
  { name: "Growth Mindset", description: "Failures are feedback, not dead ends" },
  { name: "Empathy Meets Accountability", description: "Balance compassion with clear expectations" },
  { name: "Sustainable Intensity", description: "Balance drive with recovery" },
];

const testimonials = [
  {
    quote: "The clarity I got from one session was worth more than months of second-guessing myself.",
    author: "Sarah M.",
    role: "Startup Founder",
  },
  {
    quote: "Finally, advice that doesn't feel generic. It understood my exact situation.",
    author: "David K.",
    role: "Engineering Manager",
  },
  {
    quote: "I use Ajayya before every difficult conversation now. Game changer.",
    author: "Priya R.",
    role: "COO",
  },
];

export default function Index() {
  return (
    <Layout>
      <SEO 
        title="AI-Powered Leadership Mentorship"
        description="Submit your toughest business challenges and receive structured, actionable guidance in a mentoring dialogue format."
      />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden section-padding bg-gradient-to-br from-cream via-background to-sage-light/30">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber/10 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-sage-light/20 rounded-full blur-3xl" />
        </div>

        <div className="container-wide relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-light/50 border border-amber/20 text-amber-dark text-sm font-medium mb-6 animate-fade-up">
                <Star className="h-4 w-4" />
                Trusted by 1,000+ leaders
              </div>
              <h1 className="text-display-xl mb-6 animate-fade-up">
                Real Problems.
                <br />
                <span className="text-gradient">Clear Leadership.</span>
              </h1>
              <p className="text-body-lg text-muted-foreground mb-8 animate-fade-up delay-100">
                Submit your toughest business challenges and receive structured, 
                actionable guidance in a mentoring dialogue format.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-up delay-200">
                <Button variant="hero" size="xl" asChild className="group">
                  <Link to="/ask">
                    Ask a Question
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button variant="hero-outline" size="xl" asChild>
                  <Link to="/explore">Explore Sessions</Link>
                </Button>
              </div>
            </div>

            {/* Ajayya Image */}
            <div className="relative animate-fade-up delay-300 hidden lg:block">
              <div className="relative">
                {/* Decorative ring */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/20 to-amber/20 blur-2xl scale-110" />
                
                {/* Image container with elegant border */}
                <div className="relative mx-auto w-80 h-80 rounded-full overflow-hidden border-4 border-background shadow-2xl">
                  <img 
                    src={ajayyaImage} 
                    alt="Ajayya - Leadership Mentor" 
                    className="w-full h-full object-cover object-top"
                    fetchPriority="high"
                  />
                </div>

                {/* Floating badge */}
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full bg-background border border-border shadow-lg">
                  <p className="font-display text-sm font-medium text-foreground">
                    Your Leadership Mentor
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Ajayya Section */}
      <section className="section-padding bg-card border-y border-border">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image for mobile */}
            <div className="relative lg:hidden animate-fade-up">
              <div className="mx-auto w-64 h-64 rounded-full overflow-hidden border-4 border-background shadow-xl">
                <img 
                  src={ajayyaImage} 
                  alt="Ajayya - Leadership Mentor" 
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 text-primary text-sm font-medium">
                <div className="w-8 h-[2px] bg-primary" />
                Meet Your Mentor
              </div>
              <h2 className="text-display-md">Wisdom Meets Technology</h2>
              <p className="text-body-md text-muted-foreground leading-relaxed">
                Ajayya combines decades of leadership wisdom with AI to deliver personalized, 
                actionable guidance. Every response draws from proven principles that have 
                helped thousands of leaders navigate their toughest challenges.
              </p>
              <div className="grid grid-cols-3 gap-6 pt-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">20+</p>
                  <p className="text-sm text-muted-foreground">Years Experience</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">5K+</p>
                  <p className="text-sm text-muted-foreground">Leaders Helped</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">97%</p>
                  <p className="text-sm text-muted-foreground">Satisfaction</p>
                </div>
              </div>
            </div>

            {/* Decorative quote card */}
            <div className="relative hidden lg:block">
              <div className="card-elevated p-8 relative">
                <Quote className="absolute -top-4 -left-4 h-12 w-12 text-primary/20" />
                <blockquote className="text-lg italic text-foreground mb-4">
                  "Leadership isn't about having all the answers. It's about asking 
                  the right questions and having the courage to act on what you discover."
                </blockquote>
                <p className="text-sm font-medium text-primary">— Ajayya</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Sessions */}
      <section className="section-padding">
        <div className="container-wide">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-primary text-sm font-medium mb-4">
              <TrendingUp className="h-4 w-4" />
              Trending Now
            </div>
            <h2 className="text-display-md mb-4">Recent Sessions</h2>
            <p className="text-body-md text-muted-foreground">
              Real challenges, real guidance. See how others are navigating leadership.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {sampleSessions.map((session, i) => (
              <div 
                key={i} 
                className="card-interactive p-6 animate-fade-up group" 
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className="flex gap-2 mb-3">
                  {session.tags.map((tag) => (
                    <span key={tag} className="session-tag">{tag}</span>
                  ))}
                </div>
                <h3 className="font-display text-lg font-medium mb-2 group-hover:text-primary transition-colors">
                  {session.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{session.preview}</p>
                <div className="flex items-center text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Zap className="h-4 w-4" /> {session.upvotes} upvotes
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button variant="outline" asChild className="group">
              <Link to="/explore">
                View All Sessions
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Principles Strip */}
      <section className="py-16 bg-gradient-to-r from-sage-light via-sage-light/50 to-sage-light relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptMCAyMGMtNC40MTggMC04LTMuNTgyLTgtOHMzLjU4Mi04IDgtOCA4IDMuNTgyIDggOC0zLjU4MiA4LTggOHoiIGZpbGw9IiM3YThiN2EiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-50" />
        <div className="container-wide relative z-10">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Lightbulb className="h-6 w-6 text-amber animate-pulse" />
            <h3 className="font-display text-xl font-medium">The Ajayya Principles</h3>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {principles.map((p, i) => (
              <Link
                key={p.name}
                to="/principles"
                className="px-5 py-3 rounded-full bg-background border border-border hover:border-primary/50 hover:shadow-md transition-all text-sm font-medium group animate-fade-up"
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <span className="group-hover:text-primary transition-colors">{p.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding">
        <div className="container-wide">
          <div className="text-center mb-12">
            <h2 className="text-display-md mb-4">What Leaders Say</h2>
            <p className="text-body-md text-muted-foreground">
              Join thousands who have found clarity through Ajayya.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div 
                key={i} 
                className="p-6 rounded-2xl bg-secondary/30 border border-border/50 animate-fade-up"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-amber text-amber" />
                  ))}
                </div>
                <p className="text-foreground mb-4 italic">"{t.quote}"</p>
                <div>
                  <p className="font-medium text-foreground">{t.author}</p>
                  <p className="text-sm text-muted-foreground">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-gradient-to-b from-background to-cream-dark/30">
        <div className="container-wide">
          <div className="text-center mb-12">
            <h2 className="text-display-md mb-4">How It Works</h2>
            <p className="text-body-md text-muted-foreground">
              Get personalized guidance in three simple steps.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: MessageSquare, title: "Share Your Challenge", desc: "Describe your real business problem in your own words", step: 1 },
              { icon: Zap, title: "Get Structured Guidance", desc: "Receive a dialogue-style response with takeaways and action steps", step: 2 },
              { icon: Users, title: "Learn from Others", desc: "Explore published sessions and discover new perspectives", step: 3 },
            ].map((item, i) => (
              <div key={i} className="text-center relative animate-fade-up" style={{ animationDelay: `${i * 100}ms` }}>
                {i < 2 && (
                  <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-primary/30 to-transparent" />
                )}
                <div className="relative inline-flex p-5 rounded-2xl bg-sage-light mb-4 group">
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                    {item.step}
                  </span>
                  <item.icon className="h-8 w-8 text-primary transition-transform group-hover:scale-110" />
                </div>
                <h3 className="font-display text-xl font-medium mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Business Gita Book Promotion */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber/5 via-transparent to-primary/5" />
        <div className="container-wide relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-light/50 border border-amber/20 text-amber-dark text-sm font-medium mb-6">
                <BookOpen className="h-4 w-4" />
                New from Ajayya
              </div>
              <h2 className="text-display-md mb-4">Business Gita</h2>
              <p className="text-xl font-display text-primary mb-4 italic">A Book for Everyone</p>
              <p className="text-body-md text-muted-foreground mb-6">
                50 conversations. 50 lessons. Timeless insight distilled into practical, 
                conversational wisdom that resonates with everyday life, work, and leadership.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="hero" size="lg" asChild className="group">
                  <a href="https://businessgita.org" target="_blank" rel="noopener noreferrer">
                    Get the Book
                    <ExternalLink className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </a>
                </Button>
                <Button variant="outline" size="lg" asChild className="group">
                  <Link to="/books">
                    Learn More About the Book
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/20 to-amber/20 blur-2xl scale-110" />
                <img
                  src={businessGitaCover}
                  alt="Business Gita 3.0 by Dr. Ajayya Kumar"
                  className="relative rounded-2xl shadow-2xl max-w-[240px] w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-amber/5" />
        <div className="container-narrow text-center relative z-10">
          <div className="animate-fade-up">
            <h2 className="text-display-md mb-4">Ready to Get Clarity?</h2>
            <p className="text-body-lg text-muted-foreground mb-8">
              Your first session is free. No credit card required.
            </p>
            <Button variant="hero" size="xl" asChild className="group">
              <Link to="/ask">
                Ask Your First Question
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
